import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from 'styled-components';

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    min-width: 40px;
  }

  .react-datepicker-wrapper {
    width: 140px;
  }

  .react-datepicker__input-container input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

const QuickSelectButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    justify-content: center;
  }
`;

const QuickButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.active ? '#3b82f6' : '#d1d5db'};
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background: ${props => props.active ? '#2563eb' : '#eff6ff'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ApplyButton = styled.button`
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #059669;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const DateRangeSelector = ({ onDateRangeChange, isLoading }) => {
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)); // 7 days ago
  const [endDate, setEndDate] = useState(new Date());
  const [activeQuickSelect, setActiveQuickSelect] = useState('7d');

  const quickSelectOptions = [
    { label: 'Today', value: 'today', days: 0 },
    { label: '7 Days', value: '7d', days: 7 },
    { label: '30 Days', value: '30d', days: 30 },
    { label: '90 Days', value: '90d', days: 90 },
    { label: 'Custom', value: 'custom', days: null }
  ];

  const handleQuickSelect = (option) => {
    setActiveQuickSelect(option.value);

    if (option.value !== 'custom') {
      const end = new Date();
      const start = option.days === 0
        ? new Date()
        : new Date(Date.now() - option.days * 24 * 60 * 60 * 1000);

      setStartDate(start);
      setEndDate(end);

      // Auto-apply for quick selects
      handleApplyDateRange(start, end);
    }
  };

  const handleApplyDateRange = (start = startDate, end = endDate) => {
    // Format dates for API (YYYY-MM-DD)
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    const formattedStartDate = formatDate(start);
    const formattedEndDate = formatDate(end);

    onDateRangeChange({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      label: activeQuickSelect === 'custom'
        ? `${formattedStartDate} to ${formattedEndDate}`
        : quickSelectOptions.find(opt => opt.value === activeQuickSelect)?.label || 'Custom Range'
    });
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setActiveQuickSelect('custom');
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setActiveQuickSelect('custom');
  };

  return (
    <DateRangeContainer>
      <DateInputGroup>
        <label>From:</label>
        <DatePicker
          selected={startDate}
          onChange={handleStartDateChange}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          maxDate={endDate}
          dateFormat="MMM dd, yyyy"
          placeholderText="Start date"
        />
      </DateInputGroup>

      <DateInputGroup>
        <label>To:</label>
        <DatePicker
          selected={endDate}
          onChange={handleEndDateChange}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          maxDate={new Date()}
          dateFormat="MMM dd, yyyy"
          placeholderText="End date"
        />
      </DateInputGroup>

      <QuickSelectButtons>
        {quickSelectOptions.map((option) => (
          <QuickButton
            key={option.value}
            active={activeQuickSelect === option.value}
            onClick={() => handleQuickSelect(option)}
            disabled={isLoading}
          >
            {option.label}
          </QuickButton>
        ))}
      </QuickSelectButtons>

      {activeQuickSelect === 'custom' && (
        <ApplyButton
          onClick={() => handleApplyDateRange()}
          disabled={isLoading || !startDate || !endDate || startDate > endDate}
        >
          {isLoading ? '⏳' : '✓ Apply'}
        </ApplyButton>
      )}
    </DateRangeContainer>
  );
};

export default DateRangeSelector;